import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Plus, Download, X, UploadCloud, FileText } from 'lucide-react'
import { toast } from 'react-toastify'
import { Breadcrumb } from '../../components/Common'
import DataTable from '../../components/DataTable/DataTable'
import exportLutService from '../../services/exportLutService'
import { currentFyLabel } from '../../utils/financialYearMonths'
import './ExportDetails.css'

const MAX_LUT_BYTES = 10 * 1024 * 1024
const LUT_FILE_PATTERN = /\.(pdf|png|jpe?g|webp)$/i

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const emptyForm = () => ({
  lutArn: '',
  filingDate: '',
  financialYear: currentFyLabel(),
  status: 'Active',
  remarks: '',
  document: null,
})

const toInputDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const formatDate = (value) => {
  const input = toInputDate(value)
  if (!input) return '-'
  const [year, month, day] = input.split('-')
  return `${day}-${month}-${year}`
}

const columns = [
  { key: 'financialYear', label: 'Financial Year', sortable: true },
  { key: 'lutArn', label: 'LUT Number', sortable: true },
  {
    key: 'filingDate',
    label: 'Filing Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  { key: 'status', label: 'Status', sortable: true },
  {
    key: 'documentOriginalName',
    label: 'Document',
    sortable: true,
    render: (value, row) => (row.hasDocument ? (value || 'Uploaded') : '-'),
  },
  {
    key: 'remarks',
    label: 'Remarks',
    sortable: true,
    render: (value) => value || '-',
  },
]

const ExportDetails = () => {
  const navigate = useNavigate()
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const loadRecords = async () => {
    try {
      setLoading(true)
      const response = await exportLutService.getAll()
      setRecords(response?.data || [])
    } catch (error) {
      console.error('Error loading export LUT details:', error)
      toast.error('Failed to load export LUT details')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRecords()
  }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm())
    setFormOpen(true)
  }

  const openEdit = (record) => {
    setEditing(record)
    setForm({
      lutArn: record.lutArn || '',
      filingDate: toInputDate(record.filingDate),
      financialYear: record.financialYear || currentFyLabel(),
      status: record.status || 'Active',
      remarks: record.remarks || '',
      document: null,
    })
    setFormOpen(true)
  }

  const handleSave = async (event) => {
    event.preventDefault()
    if (!form.lutArn.trim() || !form.filingDate || !form.financialYear.trim()) {
      toast.error('LUT number, filing date and financial year are required')
      return
    }
    try {
      setSaving(true)
      const response = await exportLutService.save(form, editing?.id)
      if (response?.success) {
        toast.success(editing ? 'LUT details updated' : 'LUT details added')
        setFormOpen(false)
        await loadRecords()
      } else {
        toast.error(response?.message || 'Failed to save LUT details')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save LUT details')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete LUT ${record.lutArn} for ${record.financialYear}?`)) return
    try {
      const response = await exportLutService.remove(record.id)
      if (response?.success) {
        toast.success('LUT details deleted')
        await loadRecords()
      } else {
        toast.error(response?.message || 'Failed to delete LUT details')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete LUT details')
    }
  }

  const acceptLutFile = (file) => {
    if (!file) return
    const allowedType = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)
    if (!allowedType && !LUT_FILE_PATTERN.test(file.name)) {
      toast.error('Upload a PDF or image of the LUT')
      return
    }
    if (file.size > MAX_LUT_BYTES) {
      toast.error('LUT document must be 10 MB or smaller')
      return
    }
    setForm((prev) => ({ ...prev, document: file }))
  }

  const handleDownload = async (record) => {
    try {
      await exportLutService.downloadDocument(record.id, record.documentOriginalName)
    } catch (error) {
      toast.error('Failed to download the LUT document')
    }
  }

  return (
    <div className="sales-dashboard export-details-page">
      <div className="breadcrumb-section">
        <Breadcrumb items={[
          { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
          { label: 'Export Details', path: '/settings/export-details', isCurrent: true },
        ]} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Export Details</h1>
          <p>LUT numbers used on export tax invoices. The active LUT for the invoice financial year is printed on the invoice.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          <Plus size={16} /> Add LUT
        </button>
      </div>

      <DataTable
        data={records}
        columns={columns}
        loading={loading}
        noCard
        className="export-details-table"
        pageSize={10}
        customActions={(row) => {
          const actions = [
            { type: 'edit', label: 'Edit', onClick: () => openEdit(row) },
            { type: 'delete', label: 'Delete', onClick: () => handleDelete(row) },
          ]
          if (row.hasDocument) {
            actions.splice(1, 0, {
              type: 'download',
              label: 'Download',
              icon: <Download size={16} />,
              onClick: () => handleDownload(row),
            })
          }
          return actions
        }}
      />

      {formOpen && createPortal(
        <div className="export-details-modal" onClick={() => setFormOpen(false)}>
          <form className="export-details-form" onClick={(event) => event.stopPropagation()} onSubmit={handleSave}>
            <div className="export-details-form-header">
              <h2>{editing ? 'Edit LUT' : 'Add LUT'}</h2>
              <button type="button" className="export-details-close" onClick={() => setFormOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="export-details-form-body">
              <label>
                LUT Number
                <input
                  value={form.lutArn}
                  onChange={(event) => setForm((prev) => ({ ...prev, lutArn: event.target.value.toUpperCase() }))}
                  placeholder="AD270626034656R"
                  required
                />
              </label>
              <div className="export-details-form-row">
                <label>
                  Filing Date
                  <input
                    type="date"
                    value={form.filingDate}
                    onChange={(event) => setForm((prev) => ({ ...prev, filingDate: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Financial Year
                  <input
                    value={form.financialYear}
                    onChange={(event) => setForm((prev) => ({ ...prev, financialYear: event.target.value }))}
                    placeholder="2026-27"
                    required
                  />
                </label>
              </div>
              <label>
                Status
                <select
                  value={form.status}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </label>

              <div className="export-details-upload-label">LUT Document</div>
              <div
                className={`lut-dropzone ${dragOver ? 'is-over' : ''} ${form.document ? 'has-file' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault()
                  setDragOver(true)
                }}
                onDragOver={(event) => {
                  event.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={(event) => {
                  event.preventDefault()
                  setDragOver(false)
                }}
                onDrop={(event) => {
                  event.preventDefault()
                  setDragOver(false)
                  acceptLutFile(event.dataTransfer.files?.[0])
                }}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    acceptLutFile(event.target.files?.[0])
                    event.target.value = ''
                  }}
                />
                {form.document ? (
                  <div className="lut-file-card">
                    <span className="lut-file-icon"><FileText size={18} /></span>
                    <span className="lut-file-meta">
                      <strong>{form.document.name}</strong>
                      <span>{formatFileSize(form.document.size)} · ready to upload</span>
                    </span>
                    <button
                      type="button"
                      className="lut-file-remove"
                      onClick={(event) => {
                        event.stopPropagation()
                        setForm((prev) => ({ ...prev, document: null }))
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="lut-drop-icon"><UploadCloud size={22} /></span>
                    <p>Drag and drop the LUT here</p>
                    <span>or click to browse · PDF or image · up to 10 MB</span>
                    {editing?.hasDocument && (
                      <span className="lut-current-file">On file: {editing.documentOriginalName || 'uploaded document'}</span>
                    )}
                  </>
                )}
              </div>

              <label>
                Remarks
                <textarea
                  value={form.remarks}
                  onChange={(event) => setForm((prev) => ({ ...prev, remarks: event.target.value }))}
                  rows={3}
                  placeholder="Optional note for this LUT"
                />
              </label>
            </div>

            <div className="export-details-form-actions">
              <button type="button" className="btn-secondary" onClick={() => setFormOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ExportDetails
